import type { MotionEditableField } from "@blings/blings-player/editor-tools";

const getParentPath = (path: string) => path.split(".").slice(0, -1).join(".");

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

const isShapeItemsPath = (parts: string[]) => {
  if (parts[0] !== "shapes") {
    return false;
  }

  for (let index = 1; index < parts.length; index += 2) {
    if (!Number.isInteger(Number(parts[index])) || parts[index + 1] !== "it") {
      return false;
    }
  }

  return true;
};

const getShapeItems = (layer: unknown, itemsPath: string) => {
  if (!isRecord(layer)) {
    return undefined;
  }

  const parts = itemsPath.split(".");
  if (!isShapeItemsPath(parts)) {
    return undefined;
  }

  let current: unknown = layer.shapes;
  for (let index = 1; index < parts.length; index += 2) {
    if (!Array.isArray(current)) {
      return undefined;
    }

    const item = current[Number(parts[index])];
    current = isRecord(item) ? item.it : undefined;
  }

  return Array.isArray(current) ? current : undefined;
};

export const areVectorsClose = (
  left: unknown,
  right: unknown,
  epsilon = 0.01
) => {
  if (!Array.isArray(left) || !Array.isArray(right)) {
    return false;
  }

  const comparableLength = Math.min(left.length, right.length);
  if (comparableLength < 2) {
    return false;
  }

  for (let index = 0; index < comparableLength; index += 1) {
    const entry = left[index];
    const rightEntry = right[index];
    if (
      typeof entry !== "number" ||
      typeof rightEntry !== "number" ||
      Math.abs(entry - rightEntry) > epsilon
    ) {
      return false;
    }
  }

  return true;
};

export const getPendingShapeFillOptions = (value: unknown) => {
  const options = {
    color: [1, 1, 1, 1],
    opacity: 100,
    name: "Fill",
  };

  if (Array.isArray(value)) {
    options.color = value as number[];
  }

  return options;
};

export const getPendingShapeOutlineOptions = (value: unknown) => {
  const options = {
    color: [0, 0, 0, 1],
    opacity: 100,
    width: 1,
    name: "Stroke",
  };

  if (Array.isArray(value)) {
    options.color = value as number[];
  }

  return options;
};

export const removeShapeStyleItem = (
  layer: unknown,
  field: MotionEditableField
) => {
  const expectedType = field.property === "fillColor" ? "fl" : "st";
  const itemPath = getParentPath(field.rootPath);
  const itemPathParts = itemPath.split(".");
  const itemIndex = itemPathParts[itemPathParts.length - 1];
  const itemsPath = itemPathParts.slice(0, -1).join(".");

  if (!Number.isInteger(Number(itemIndex))) {
    return false;
  }

  const items = getShapeItems(layer, itemsPath);
  const item = items?.[Number(itemIndex)];
  if (!isRecord(item) || item.ty !== expectedType) {
    return false;
  }

  items.splice(Number(itemIndex), 1);
  return true;
};
